import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { type LoginRequest, LoginRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthServices } from '../auth-services-context';
import { useAuthState } from '../auth-state-context';
import { AuthShell } from '../components/AuthShell';
import { ErrorBanner } from '../components/ErrorBanner';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const [error, setError] = useState<unknown>(null);
  const [showPassword, setShowPassword] = useState(false);
  const auth = useAuthServices();
  const { setSession, setMfaIntent, sessionExpired, sessionExpiredReason, clearSessionExpired } =
    useAuthState();
  const navigate = useNavigate();

  // Captured once at mount so the notice this render is showing doesn't
  // vanish the instant clearSessionExpired() flips the store's flag below —
  // a voluntary sign-out never sets sessionExpired, so it renders nothing.
  const [showSessionExpiredNotice] = useState(sessionExpired);
  // QA-I11: the refresh was rejected because the organization is suspended.
  const [noticeKey] = useState(
    sessionExpiredReason === 'organization_suspended' ? 'orgSuspended' : 'sessionExpired',
  );

  // Runs once on mount: this notice should not stay stuck for a later
  // visit, but re-running it on every sessionExpired change would just
  // call clearSessionExpired repeatedly for no benefit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect, see comment above
  useEffect(() => {
    if (sessionExpired) clearSessionExpired();
  }, []);

  const form = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  async function onSubmit(values: LoginRequest) {
    setError(null);
    try {
      const result = await auth.login(values);
      if (result.kind === 'mfa-required') {
        setMfaIntent(result.intent);
        await navigate({ to: result.redirectTo });
        return;
      }
      setSession(result.session);
      await navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err);
    }
  }

  const { errors, isSubmitting } = form.formState;

  return (
    <AuthShell
      title={t('login.title')}
      eyebrow={t('login.eyebrow')}
      description={t('login.description')}
      aside={{
        illustration: 'valet',
        eyebrow: t('login.aside.eyebrow'),
        headline: t('login.aside.headline'),
        body: t('login.aside.body'),
      }}
      footer={
        <p>
          {t('login.needHelp')}{' '}
          <Link
            to="/forgot-password"
            className="font-medium text-jq-navy underline underline-offset-2"
          >
            {t('login.resetPasswordLink')}
          </Link>
        </p>
      }
    >
      <form className="grid gap-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {showSessionExpiredNotice ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-sm border border-jq-gold/60 bg-jq-gold/10 px-3 py-2 text-[13px] font-medium text-jq-navy"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-jq-gold" />
            <span>
              <strong className="font-semibold">{t(`${noticeKey}.title`)}</strong>{' '}
              {t(`${noticeKey}.body`)}
            </span>
          </div>
        ) : null}

        <ErrorBanner error={error} />

        <div className="grid gap-2">
          <Label htmlFor="email">{t('login.emailLabel')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('login.emailPlaceholder')}
            aria-invalid={!!errors.email}
            {...form.register('email')}
          />
          {errors.email ? (
            <p className="text-[13px] text-jq-danger">{errors.email.message}</p>
          ) : null}
        </div>

        <div className="grid gap-2">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="password">{t('login.passwordLabel')}</Label>
            <Link
              to="/forgot-password"
              className="text-[12px] font-medium text-jq-navy underline-offset-2 hover:underline"
            >
              {t('login.forgotIt')}
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              className="pr-10"
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-jq-graphite transition hover:text-jq-navy"
              aria-label={showPassword ? t('shared.hidePassword') : t('shared.showPassword')}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          {errors.password ? (
            <p className="text-[13px] text-jq-danger">{errors.password.message}</p>
          ) : null}
        </div>

        <Button type="submit" disabled={isSubmitting} className="mt-1 w-full">
          {isSubmitting ? t('login.submitting') : t('login.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
